"use client";

import { type FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { JaaSMeeting } from "@jitsi/react-sdk";
import type { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";
import {
  Backdrop,
  Button,
  Card,
  Center,
  Chip,
  Icon,
  IconButton,
  Modal,
  Popup,
  Reveal,
  SelectList,
  Text,
  type Timeout,
  Tooltip,
  Flex,
  useClickOutside,
  ProgressBar,
  useDragReposition,
  nextFrame,
  transitionDurations,
  Portal,
  zIndexes,
  useDebug,
} from "@huub-web/ui";
import { formatDuration, isWindowDefined, mergeClasses } from "@huub-web/utils";

import { darkTheme } from "@huub-web/ui/theme/css/darkTheme.css";

import {
  cardStyle,
  closeButtonStyle,
  wrapperStyle,
  contentStyle,
  dragHandleStyle,
  containerStyle,
  presenceAnimationStyle,
  toolbarStyle,
} from "./VideoCall.css";

/**
 * This component is a wrapper around the JitsiMeetExternalAPI. It implements custom UI elements
 * and some additional logic.
 *
 * @todo Too big. Divide into sub components.
 * @todo Recording.
 *
 * @see https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-react-sdk
 * @see https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-iframe-functions/
 * @see https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-iframe-commands/
 * @see https://jitsi.github.io/handbook/docs/dev-guide/dev-guide-iframe-events/
 * @see https://github.com/jitsi/jitsi-meet/blob/master/interface_config.js
 */

type VideoCallProps = {
  defaultLanguage?: string;
  userInfo?: {
    displayName?: string;
    email?: string;
  };
  roomName?: string;
  appID: string;
  onClose?: () => void;
};

export const VideoCall: FC<VideoCallProps> = ({
  defaultLanguage = "nl",
  roomName = "Meeting",
  userInfo,
  appID,
  onClose,
}) => {
  const { log } = useDebug();

  const [isConnected, setIsConnected] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isRecording /*, setIsRecording*/] = useState(false);
  const [canRejoin, setCanRejoin] = useState(false);
  const [variant, setVariant] = useState<"none" | "small" | "large">("small");
  const [isCloseConfirmModalOpen, setIsCloseConfirmModalOpen] = useState(false);
  const [isSettingsPopupActive, setIsSettingsPopupActive] = useState(false);
  const [meetingDuration, setMeetingDuration] = useState(0);

  const apiRef = useRef<IJitsiMeetExternalApi | null>(null);
  const timerRef = useRef<Timeout | null>(null);
  const settingsPopupRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const { isDragging } = useDragReposition(handleRef, targetRef, dragActive);

  useClickOutside<HTMLButtonElement | HTMLDivElement>(
    [settingsPopupRef, settingsButtonRef],
    () => setIsSettingsPopupActive(false),
  );

  const initialize = async () => {
    if (!apiRef.current) {
      return;
    }

    const isAudioMuted = await apiRef.current.isAudioMuted();
    const isVideoMuted = await apiRef.current.isVideoMuted();

    apiRef.current.addListener("audioMuteStatusChanged", ({ muted }) => {
      log("red", "Meet", "audioMuteStatusChanged", muted);
      setIsAudioMuted(muted);
    });
    apiRef.current.addListener("videoMuteStatusChanged", ({ muted }) => {
      log("red", "Meet", "videoMuteStatusChanged", muted);
      setIsVideoMuted(muted);
    });

    // apiRef.current.addListener("recordingStarted", () => {
    //   setIsRecording(true);
    // });
    // apiRef.current.addListener("recordingStopped", () => {
    //   setIsRecording(false);
    // });

    setIsAudioMuted(isAudioMuted);
    setIsVideoMuted(isVideoMuted);

    setTimeout(() => {
      setIsConnected(true);
    }, 500);
  };

  const handleAPIReadyEvent = async (api: IJitsiMeetExternalApi) => {
    log("red", "Meet", "API ready event");

    apiRef.current = api;
    initialize();

    // Start timer
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setMeetingDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
  };

  const handleAPILeftEvent = () => {
    log("red", "Meet", "API left event");
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    hangup();
  };

  const handleHangup = () => {
    if (apiRef.current) {
      apiRef.current.executeCommand("hangup");
    }
  };

  const hangup = () => {
    setIsConnected(false);
    setMeetingDuration(0);
    setCanRejoin(true);
    if (apiRef.current) {
      apiRef.current.dispose();
      apiRef.current = null;
    }
  };

  const handleRejoin = () => {
    setCanRejoin(false);
  };

  const handleExpandToggle = () => {
    setVariant(variant === "small" ? "large" : "small");
  };

  const handleClose = () => {
    setIsCloseConfirmModalOpen(true);
  };

  const handleCloseModalClose = () => {
    setIsCloseConfirmModalOpen(false);
    handleHangup();
    setVariant("none");
    hangup();
    onClose?.();
  };

  const handleRecordToggle = () => {
    if (!apiRef.current) {
      return;
    }

    if (isRecording) {
      apiRef.current.executeCommand("stopRecording");
    } else {
      apiRef.current.executeCommand("startRecording");
    }
  };

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useLayoutEffect(() => {
    if (!targetRef.current || !isMounted || !isWindowDefined()) {
      return;
    }

    targetRef.current.style.left = "";

    if (variant === "large") {
      targetRef.current.style.top = "50%";
      targetRef.current.style.right = "50%";
    } else {
      targetRef.current.style.top = "32px";
      targetRef.current.style.right = `calc(50% - ${targetRef.current.clientWidth / 2}px)`;
    }
  }, [variant, isMounted]);

  useEffect(() => {
    if (variant === "small") {
      setDragActive(true);
    } else {
      setDragActive(false);
    }

    nextFrame(() => {
      if (!targetRef.current) {
        return;
      }

      if (variant === "large") {
        targetRef.current.style.top = "50%";
        targetRef.current.style.right = "50%";
      } else {
        targetRef.current.style.top = "32px";
        targetRef.current.style.right = `calc(50% - ${targetRef.current.clientWidth / 2}px)`;
      }
    });
  }, [variant]);

  if (variant === "none") {
    return null;
  }

  return (
    <Portal>
      {variant === "large" && <Backdrop />}

      <div
        ref={targetRef}
        className={mergeClasses(darkTheme, containerStyle)}
        style={{
          transform: variant === "large" ? "translate(50%, -50%)" : undefined,
          maxWidth: variant === "large" ? "80vw" : "360px",
          zIndex: zIndexes.portal,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: transitionDurations.m,
            ease: "easeInOut",
          }}
        >
          <Card
            variant="tertiary"
            padding="none"
            elevated
            className={cardStyle}
            overflowHidden
          >
            <Flex
              className={wrapperStyle}
              direction="column"
              align="stretch"
              justify="space-between"
            >
              <div className={contentStyle}>
                {!canRejoin && (
                  <JaaSMeeting
                    appId={appID}
                    roomName={roomName}
                    configOverwrite={{
                      hideConferenceTimer: true,
                      disableModeratorIndicator: true,
                      hideLobbyButton: true,
                      disableInviteFunctions: true,
                      toolbarButtons: [],
                      filmStripOnly: false,
                      disableChat: true,
                      disableReactions: true,
                      defaultLanguage,
                      hideConferenceSubject: true,
                      lobby: {
                        autoKnock: false,
                        enableChat: false,
                      },
                      prejoinConfig: {
                        enabled: false,
                      },
                      prejoinPageEnabled: false,
                      startWithAudioMuted: false,
                      startWithVideoMuted: false,
                      hideDisplayName: true,
                      hideConferenceName: true,
                      hideParticipantsStats: true,
                      disableAddingBackgroundImages: true,
                    }}
                    interfaceConfigOverwrite={{
                      TOOLBAR_ALWAYS_VISIBLE: false,
                      DEFAULT_BACKGROUND: "#000",
                      VIDEO_QUALITY_LABEL_DISABLED: true,
                      SHOW_JITSI_WATERMARK: false,
                      SHOW_WATERMARK_FOR_GUESTS: false,
                      SHOW_BRAND_WATERMARK: false,
                      SHOW_POWERED_BY: false,
                      DISABLE_VIDEO_BACKGROUND: true,
                    }}
                    userInfo={{
                      displayName: userInfo?.displayName ?? "",
                      email: userInfo?.email ?? "",
                    }}
                    onApiReady={handleAPIReadyEvent}
                    onReadyToClose={handleAPILeftEvent}
                    getIFrameRef={(iframe) => {
                      if (iframe) {
                        iframe.style.width = "100%";
                        iframe.style.height = "100%";
                        iframe.style.border = "none";
                      }
                    }}
                  />
                )}
              </div>

              {variant === "small" && (
                <Tooltip
                  value={"Verslepen"}
                  disabled={isDragging}
                  placement="bottom"
                  targetRef={handleRef}
                >
                  <IconButton
                    as="div"
                    icon="drag"
                    size="s"
                    variant="opaque"
                    active={isDragging}
                    className={dragHandleStyle}
                  />
                </Tooltip>
              )}

              {(variant === "small" || (variant === "large" && canRejoin)) && (
                <Tooltip value="Afsluiten" placement="bottom">
                  <IconButton
                    icon="closeSmall"
                    size="s"
                    variant="opaque"
                    rounded
                    onClick={handleClose}
                    className={closeButtonStyle}
                  />
                </Tooltip>
              )}

              <Reveal className={presenceAnimationStyle}>
                {meetingDuration > 0 && (
                  <Chip
                    leading={<Icon icon="callFilled" size="s" />}
                    variant="secondary"
                  >
                    {formatDuration(meetingDuration, {
                      timeFormat: true,
                    })}
                  </Chip>
                )}
              </Reveal>

              {canRejoin && (
                <Center grow>
                  <Button
                    variant="primary"
                    size="s"
                    onClick={handleRejoin}
                    trailing={<Icon icon="reload" size="s" />}
                  >
                    Herverbinden
                  </Button>
                </Center>
              )}

              <Flex gap="xs" justify="center" className={toolbarStyle}>
                <Tooltip
                  value={variant === "small" ? "Uitvergroten" : "Verkleinen"}
                  disabled={!isConnected}
                >
                  <IconButton
                    icon={
                      variant === "small"
                        ? "squareArrowExpand"
                        : "squareArrowShrink"
                    }
                    disabled={!isConnected}
                    size="s"
                    onClick={handleExpandToggle}
                    variant="secondary"
                  />
                </Tooltip>

                <Tooltip
                  value={isAudioMuted ? "Microfoon aan" : "Microfoon uit"}
                  disabled={!isConnected}
                >
                  <IconButton
                    icon={isAudioMuted ? "microphoneOff" : "microphone"}
                    size="s"
                    variant="secondary"
                    disabled={!isConnected}
                    onClick={() => {
                      if (apiRef.current) {
                        apiRef.current.executeCommand("toggleAudio");
                      }
                    }}
                  />
                </Tooltip>

                <Tooltip
                  value={isVideoMuted ? "Camera aan" : "Camera uit"}
                  disabled={!isConnected}
                >
                  <IconButton
                    icon={isVideoMuted ? "videoOff" : "video"}
                    size="s"
                    variant="secondary"
                    disabled={!isConnected}
                    onClick={() => {
                      if (apiRef.current) {
                        apiRef.current.executeCommand("toggleVideo");
                      }
                    }}
                  />
                </Tooltip>

                <Tooltip value={"Ophangen"} disabled={!isConnected}>
                  <IconButton
                    icon="callEndFilled"
                    disabled={!isConnected}
                    size="s"
                    variant="error"
                    onClick={handleHangup}
                  />
                </Tooltip>

                <Popup
                  ref={settingsPopupRef}
                  active={isSettingsPopupActive}
                  placement="bottomLeft"
                  applyActive
                  onClose={() => setIsSettingsPopupActive(false)}
                  escape
                  size="minimal"
                  content={
                    <SelectList
                      options={[
                        {
                          value: "shareScreen",
                          label: "Scherm delen",
                          leading: <Icon icon="screenShare" size="s" />,
                        },
                        {
                          value: "record",
                          label: isRecording
                            ? "Opname stoppen"
                            : "Meeting opnemen",
                          leading: <Icon icon="video" size="s" />,
                          disabled: true,
                        },
                      ]}
                      onSelect={(option) => {
                        if (option.value === "shareScreen") {
                          if (apiRef.current) {
                            apiRef.current.executeCommand("toggleShareScreen");
                          }
                        }
                        if (option.value === "record") {
                          handleRecordToggle();
                        }
                        setIsSettingsPopupActive(false);
                      }}
                    />
                  }
                >
                  <IconButton
                    ref={settingsButtonRef}
                    icon="threeDots"
                    disabled={!isConnected}
                    size="s"
                    variant="secondary"
                    onClick={() => setIsSettingsPopupActive(true)}
                  />
                </Popup>
              </Flex>
            </Flex>

            {!canRejoin && !isConnected && (
              <ProgressBar variant="infinite" position="absolute" />
            )}
          </Card>
        </motion.div>
      </div>

      {isCloseConfirmModalOpen && (
        <Modal
          title="Afsluiten"
          placement="center"
          onClose={() => setIsCloseConfirmModalOpen(false)}
          escape
        >
          <Text>Weet je zeker dat je de meeting wilt afsluiten?</Text>
          <Flex gap="xs" justify="space-between">
            <Button
              onClick={() => setIsCloseConfirmModalOpen(false)}
              variant="primary"
              border="default"
            >
              Annuleren
            </Button>
            <Button onClick={handleCloseModalClose} variant="secondary">
              Afsluiten
            </Button>
          </Flex>
        </Modal>
      )}
    </Portal>
  );
};
